import { useEffect, useRef, useState } from "react";
import { Brand } from "./Brand";
import type { DecodedCode, FrameRequest, FrameResult } from "./scan/scan.worker.js";

/** Run the camera pipeline (localise + decode, in the worker) on a photo from the device,
 *  drawn at camera resolution, and show the outlines it would draw. Lets labels be tested
 *  from the photo library instead of pointing the camera at a screen. */
export function PhotoTest({ onClose }: { onClose: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const workerRef = useRef<Worker | null>(null);
  const [codes, setCodes] = useState<DecodedCode[]>([]);
  const [info, setInfo] = useState("Choose a photo.");

  useEffect(() => {
    const w = new Worker(new URL("./scan/scan.worker.ts", import.meta.url), { type: "module" });
    workerRef.current = w;
    // ?photo=<url> runs on that image at once (used for automated checks).
    const url = new URLSearchParams(window.location.search).get("photo");
    if (url) void fetch(url).then((r) => r.blob()).then((b) => run(new File([b], "photo")));
    return () => w.terminate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (file: File) => {
    const canvas = canvasRef.current;
    const worker = workerRef.current;
    if (!canvas || !worker) return;
    setInfo("Loading…");
    const bitmap = await createImageBitmap(file);
    // Same geometry as the camera at 4K: at most 3840 px on the long side.
    const k = Math.min(1, 3840 / Math.max(bitmap.width, bitmap.height));
    canvas.width = Math.round(bitmap.width * k);
    canvas.height = Math.round(bitmap.height * k);
    const g = canvas.getContext("2d")!;
    g.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const img = g.getImageData(0, 0, canvas.width, canvas.height);
    const buffer = img.data.buffer as ArrayBuffer;
    setInfo(`Scanning ${canvas.width}×${canvas.height}…`);
    const result = await new Promise<FrameResult>((resolve) => {
      worker.onmessage = (ev: MessageEvent<FrameResult>) => resolve(ev.data);
      const msg: FrameRequest = { type: "frame", data: buffer, width: canvas.width, height: canvas.height, maxDecodes: 12 };
      worker.postMessage(msg, [buffer]);
    });
    setCodes(result.codes);
    setInfo(`${result.regions} regions · ${result.codes.length} codes · locate ${result.locateMs} ms · total ${result.totalMs} ms`);
    g.lineWidth = 4;
    for (const c of result.codes) {
      g.strokeStyle = "#22c55e";
      g.fillStyle = "rgba(34,197,94,0.18)";
      g.beginPath();
      g.moveTo(c.corners[0].x, c.corners[0].y);
      for (let i = 1; i < 4; i++) g.lineTo(c.corners[i]!.x, c.corners[i]!.y);
      g.closePath();
      g.fill();
      g.stroke();
    }
  };

  return (
    <main>
      <Brand title="photo test" />
      <div className="panel">
        <div className="row">
          <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) void run(f); }} />
          <button type="button" className="secondary" onClick={onClose}>Back</button>
        </div>
        <p className="muted">{info}</p>
      </div>
      <canvas ref={canvasRef} style={{ width: "100%", height: "auto", display: "block", borderRadius: 8 }} />
      {codes.length > 0 && (
        <div className="panel">
          <ul className="hri">
            {codes.map((c, i) => (
              <li key={i}>{c.format} · {c.scanData.replaceAll("\u001d", "⟨GS⟩")} · {Math.round((c.angle * 180) / Math.PI)}°</li>
            ))}
          </ul>
        </div>
      )}
    </main>
  );
}
